import React, { useEffect, useRef, useState, useMemo } from "react";
import {
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  Grid,
  Stack,
  Avatar,
  Paper,
  Divider,
  Table,
  TableRow,
  TableBody,
  TableCell,
  Tooltip,
  useTheme,
  useMediaQuery,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  Switch,
  MenuItem,
  TablePagination,
  Pagination,
  Button,
  CircularProgress,
  Menu,
  IconButton,
  TableHead,
  Chip,
} from "@mui/material";
import { useAuth } from "context/authContext";
import { useTranslation } from "react-i18next";
import SnackbarWrapper from "components/Snackbar";
import LoadingScreen from "components/Loading";
import {
  getAssessmentPageKpis,
  getQuarterlyAssessmentKpis,
  getFunctionalAssessments,
  getFunctionalAssessmentsQuarterly,
} from "services/api";
import MDButton from "components/MDButton";
import PropTypes from "prop-types";
import { AttachFile, Clear, TrendingDown, TrendingFlat, TrendingUp } from "@mui/icons-material";
import { calculateKpiResult } from "services/utils";
import { numberTextFieldStyling } from "services/utils";
import { updatePerformance } from "services/api";
import EvidenceDownloadButton from "components/ReusableEvidenceDownload";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { addEvidence } from "services/api";
import CommentButton from "components/CommentButton";
import ConfirmationDialog from "components/ConfirmationDialog";
import { deleteEvidence } from "services/api";
import { addCommentToEvidence } from "services/api";
import DescriptionPopover from "components/DescriptionPopover";
import KpiTableRow from "./components/TableRow";

const compactInputStyle = {
  width: "100%",
  maxWidth: 100, // Slightly smaller than the cell to allow for cell padding
  "& .MuiInputBase-input": {
    padding: "4px 2px", // Minimal horizontal padding
    fontSize: "0.875rem",
    textAlign: "center",
    // Hide the "spinners" (up/down arrows)
    "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
      "-webkit-appearance": "none",
      margin: 0,
    },
    "-moz-appearance": "textfield", // Hide for Firefox
  },
  "& .MuiOutlinedInput-root": {
    borderRadius: "4px",
    backgroundColor: "#fff",
  },
};

const getStatusColor = (result) => {
  if (result == null) return "grey";
  if (result > 105) return "blue";
  if (result >= 90) return "green";
  if (result >= 80) return "yellow";
  if (result >= 70) return "orange";
  return "red";
};

const getTrendIcon = (trend, achievement) => {
  if (trend == null || achievement == null) return null;
  const delta = trend - achievement;
  if (delta > 0) return <TrendingUp sx={{ color: "green" }} />;
  if (delta < 0) return <TrendingDown sx={{ color: "red" }} />;
  return <TrendingFlat sx={{ color: "gray" }} />;
};

const ClampedCell = ({ text }) => (
  <Tooltip title={text}>
    <Box
      sx={{
        maxWidth: 130,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {text}
    </Box>
  </Tooltip>
);

function AssessorKpiPage({ isPrimary }) {
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 2022 + 1 }, (_, i) => 2023 + i);
  const { t, i18n } = useTranslation();
  const snackbarRef = useRef();
  const { employee, selectedMonth, selectedYear, quarter, isQuarterly } = useAuth();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [view, setView] = useState("byEmployee"); // "byEmployee" or "byKPI"
  const [employeeFilter, setEmployeeFilter] = useState("");
  const [showOnlyUnassessed, setShowOnlyUnassessed] = useState(false);
  const [achievements, setAchievements] = useState({});
  const [targets, setTargets] = useState({});
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5); // number of employees (groups) per page
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [openKpiId, setOpenKpiId] = useState(null);
  const [currentAssessments, setCurrentAssessments] = useState([]);
  const [loadingKpiId, setLoadingKpiId] = useState(null);
  const [confOpen, setConfOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [expandedEmployee, setExpandedEmployee] = useState(null);
  useEffect(() => {
    if (!employee?.id) return;

    setLoading(true);
    if (data != []) {
      setData([]);
      setView("byEmployee");
      setAchievements({});
      setTargets({});
    }

    let assessData;
    if (isQuarterly) {
      assessData = getQuarterlyAssessmentKpis(employee.id, quarter, selectedYear, isPrimary);
    } else {
      assessData = getAssessmentPageKpis(employee.id, selectedMonth, selectedYear, isPrimary);
    }

    assessData
      .then((res) => {
        if (res !== "error") {
          const initialAchievements = {};

          const initialTargets = {};

          const processedData = res.map((item) => {
            const id = `${item.kpiAssignmentsId}_${item.employeeId}`;
            const achievement = isPrimary ? item.kpiAchievement : item.secondaryAchievement;
            const target = item.kpiTarget;

            initialAchievements[id] = achievement;
            initialTargets[id] = target;

            const averageResult = calculateKpiResult(
              target,
              achievement,
              isPrimary ? achievement : target,
              item.kpiFormula,
              item.primaryPercentage
            );

            return {
              ...item,
              averageResult: averageResult ?? null, // fallback to null if undefined
            };
          });
          setAchievements(initialAchievements);
          setTargets(initialTargets);
          setData(processedData);
        }
      })
      .finally(() => setLoading(false));
  }, [employee.id, selectedMonth, selectedYear, quarter, isPrimary, isQuarterly]);

  const handleAchievementChange = (id, value) => {
    setAchievements((prev) => ({ ...prev, [id]: value }));
  };

  const handleTargetChange = (id, value) => {
    setTargets((prev) => ({ ...prev, [id]: value }));
  };

  const handleOpenAssessments = async (event, item) => {
    const key = `${item.kpiAssignmentsId}_${item.employeeId}`;
    setLoadingKpiId(key);
    setMenuAnchor(event.currentTarget);
    setOpenKpiId(key);

    try {
      let result;
      if (isQuarterly) {
        result = await getFunctionalAssessmentsQuarterly(
          quarter,
          selectedYear,
          item.employeeId,
          item.kpiAssignmentsId
        );
      } else {
        result = await getFunctionalAssessments(
          selectedMonth,
          selectedYear,
          item.employeeId,
          item.kpiAssignmentsId
        );
      }

      const assessments = Array.isArray(result) ? result : [result];
      setCurrentAssessments(assessments);
    } catch (err) {
      console.error("Error fetching functional assessments", err);
      setCurrentAssessments([]);
    } finally {
      setLoadingKpiId(null); // clear loading state
    }
  };

  const handleCloseAssessments = () => {
    setMenuAnchor(null);
    setOpenKpiId(null);
    setCurrentAssessments([]);
  };

  const handleUpdateKpi = async (id, field) => {
    // 1. Identify the KPI and its current state
    const originalItem = data.find((item) => `${item.kpiAssignmentsId}_${item.employeeId}` === id);
    if (!originalItem) return;

    // 2. Helper to convert "" to null
    const normalizeValue = (val) => {
      if (val === "" || val === undefined || val === null) return null;
      return val;
    };

    const currentTarget = normalizeValue(targets[id]);
    const currentAchievement = normalizeValue(achievements[id]);

    // 3. Check if the specific field being saved actually changed
    const isTargetField = field === "target";
    const oldValue = isTargetField ? originalItem.kpiTarget : originalItem.kpiAchievement;
    const newValue = isTargetField ? currentTarget : currentAchievement;

    // Stop if the value is the same (comparing null to null or "5" to "5")
    if (normalizeValue(oldValue) === newValue) return;

    // 4. Prepare API parameters
    const [kpiAssignmentsIdStr, employeeIdStr] = id.split("_");
    const kpiAssignmentsId = parseInt(kpiAssignmentsIdStr, 10);
    const employeeId = employeeIdStr;
    const month = !isQuarterly ? selectedMonth : quarter >= 1 && quarter <= 4 ? quarter * 3 : 0;

    if (month === 0) return;

    // 5. API Call
    const result = await updatePerformance(
      employeeId,
      kpiAssignmentsId,
      month,
      selectedYear,
      currentTarget, // Sends null if empty
      currentAchievement, // Sends null if empty
      isPrimary
    );

    if (result !== "error") {
      setData((prev) =>
        prev.map((item) => {
          if (`${item.kpiAssignmentsId}_${item.employeeId}` === id) {
            // Recalculate average using the new normalized values
            const updatedAverage =
              calculateKpiResult(
                currentTarget,
                currentAchievement,
                isPrimary ? item.secondaryAchievement : currentTarget,
                item.kpiFormula,
                item.primaryPercentage
              ) ?? null;

            return {
              ...item,
              kpiTarget: currentTarget,
              kpiAchievement: currentAchievement,
              averageResult: updatedAverage,
              // Ensure performanceId is captured if it was just created
              performanceId: result.performanceId || item.performanceId,
            };
          }
          return item;
        })
      );

      snackbarRef.current?.showSnackbar(
        isTargetField
          ? t("user.myScorecard.snackbar.targetUpdated")
          : t("user.functional.achievementSaved"),
        "success"
      );
    } else {
      snackbarRef.current?.showSnackbar(t("user.functional.error"), "error");
    }
  };

  const handleFileAttach = async (event, kpi, isSelfAchievement) => {
    const file = event.target.files[0];
    if (!file) return;

    const maxSize = 20 * 1024 * 1024; // 20 MB
    if (file.size > maxSize) {
      snackbarRef.current?.showSnackbar(t("user.myScorecard.evidence.FileSizeLimit"), "error");
      event.target.value = null; // Reset file input
      return;
    }

    try {
      let result = null;

      // CASE 1: performanceId exists — directly upload the evidence
      if (kpi.performanceId) {
        result = await addEvidence(kpi.performanceId, file, isSelfAchievement);
      } else {
        // CASE 2: performanceId does not exist — create performance first

        let monthToSend = selectedMonth;
        if (isQuarterly) {
          // monthToSend = { Q1: 3, Q2: 6, Q3: 9, Q4: 12 }[quarter] ?? null;
          const mappedMonth = { 1: 3, 2: 6, 3: 9, 4: 12 }[quarter];
          monthToSend = Number.isInteger(mappedMonth) ? mappedMonth : selectedMonth;
        }

        const updateResult = await updatePerformance(
          kpi.employeeId,
          kpi.kpiAssignmentsId,
          // selectedMonth,
          monthToSend,
          selectedYear,
          kpi.kpiTarget
        );

        if (updateResult !== "error") {
          const newPerformanceId = updateResult.performanceId;

          // Now upload the evidence with the new performanceId
          result = await addEvidence(newPerformanceId, file, isSelfAchievement);

          // Update frontend state to reflect new performanceId
          setData((prevData) => {
            const updated = prevData.map((item) =>
              item.kpiAssignmentsId === kpi.kpiAssignmentsId && item.employeeId === kpi.employeeId
                ? {
                    ...item,
                    performanceId: newPerformanceId,
                    fileName: file.name,
                  }
                : item
            );
            return updated;
          });
        } else {
          snackbarRef.current?.showSnackbar(
            t("user.myScorecard.evidence.CreatePerformanceError"),
            "error"
          );
          return;
        }
      }
      // Show success snackbar if file upload succeeded
      if (result && result != "error") {
        snackbarRef.current?.showSnackbar(
          t("user.myScorecard.evidence.EvidenceUploadSuccess"),
          "success"
        );

        // Update filename in UI
        // 1. Determine which field name to update
        const fileNameField = isSelfAchievement ? "evidence" : "assessorEvidence";
        setData((prevData) => {
          const updated = prevData.map((item) =>
            // 2. Find the correct KPI by performanceId
            item.performanceId === (kpi.performanceId || result.performanceId)
              ? {
                  ...item,
                  // 3. Use the computed property name to update either fileName or assessorFileName
                  [fileNameField]: file.name,
                }
              : item
          );

          return updated;
        });
      }
    } catch (err) {
      snackbarRef.current?.showSnackbar(
        t("user.myScorecard.evidence.EvidenceUploadFailure"),
        "error"
      );
    }

    // Clear file input to allow re-uploading same file
    event.target.value = null;
  };

  const handleDeleteEvidence = async () => {
    // 1. Optional: Add a confirmation dialog
    const item = itemToDelete;
    try {
      // 2. Call your API (Assuming you have a deleteEvidence function)
      // If you don't have one, this usually involves updatePerformance with a null file
      const result = await deleteEvidence(item.performanceId);

      if (result !== "error") {
        const fieldName = "assessorEvidence";

        setData((prevData) =>
          prevData.map((prevItem) =>
            prevItem.performanceId === item.performanceId
              ? { ...prevItem, [fieldName]: null }
              : prevItem
          )
        );
        snackbarRef.current?.showSnackbar(t("user.myScorecard.evidence.DeleteSuccess"), "success");
      }
    } catch (err) {
      snackbarRef.current?.showSnackbar(t("user.myScorecard.evidence.DeleteError") + err, "error");
    }

    setConfOpen(false);
  };

  const handleSaveComment = async (key, newComment) => {
    // 1. Find the specific KPI item from your current data state using the unique key
    const kpi = data.find((item) => `${item.kpiAssignmentsId}_${item.employeeId}` === key);
    if (!kpi) return;

    try {
      let currentPerformanceId = kpi.performanceId;

      // CASE: performanceId does not exist — create performance record first
      if (!currentPerformanceId) {
        let monthToSend = selectedMonth;
        if (isQuarterly) {
          const mappedMonth = { 1: 3, 2: 6, 3: 9, 4: 12 }[quarter];
          monthToSend = Number.isInteger(mappedMonth) ? mappedMonth : selectedMonth;
        }

        // Create the performance entry
        const updateResult = await updatePerformance(
          kpi.employeeId,
          kpi.kpiAssignmentsId,
          monthToSend,
          selectedYear,
          targets[key] || kpi.kpiTarget, // Use existing target from state or item
          achievements[key] || kpi.kpiAchievement, // Use existing achievement
          isPrimary
        );

        if (updateResult !== "error" && updateResult.performanceId) {
          currentPerformanceId = updateResult.performanceId;
        } else {
          snackbarRef.current?.showSnackbar(
            t("user.myScorecard.evidence.CreatePerformanceError"),
            "error"
          );
          return;
        }
      }

      // Now that we definitely have a performanceId, save the comment
      const result = await addCommentToEvidence(currentPerformanceId, newComment);

      if (result !== "error") {
        // Update frontend state
        setData((prevData) =>
          prevData.map((item) => {
            const itemKey = `${item.kpiAssignmentsId}_${item.employeeId}`;
            if (itemKey === key) {
              return {
                ...item,
                performanceId: currentPerformanceId, // Ensure the new ID is stored
                comment: newComment, // Update the comment field
              };
            }
            return item;
          })
        );

        snackbarRef.current?.showSnackbar(t("user.functional.commentSaved"), "success");
      } else {
        snackbarRef.current?.showSnackbar(t("user.functional.error"), "error");
      }
    } catch (err) {
      console.error("Error in handleSaveComment:", err);
      snackbarRef.current?.showSnackbar(t("user.functional.error"), "error");
    }
  };

  const handleConfOpen = (item) => {
    setConfOpen(true);
    setItemToDelete(item);
  };

  // Keep your existing pagination logic
  const [paginatedGroups, groupedEntries, totalGroupCount] = useMemo(() => {
    const groups = {};

    const filtered =
      data?.filter((item) => {
        const nameToCheck = view === "byEmployee" ? item.employeeName : item.kpiName;
        const matchesEmployeeFilter =
          !employeeFilter || nameToCheck.toLowerCase().includes(employeeFilter.toLowerCase());
        const matchesUnassessedFilter = !showOnlyUnassessed || item.kpiAchievement == null;
        return matchesEmployeeFilter && matchesUnassessedFilter;
      }) || [];

    filtered.forEach((item) => {
      const key = view === "byEmployee" ? item.employeeName : item.kpiName;
      if (!groups[key]) groups[key] = [];
      groups[key].push(item);
    });

    const groupedEntries = Object.entries(groups);
    const sliced = groupedEntries.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return [sliced, groupedEntries, groupedEntries.length];
  }, [data, view, employeeFilter, showOnlyUnassessed, page, rowsPerPage]);

  // NEW: Transform the visible items on the current page into Perspective Groups
  const perspectiveGroups = useMemo(() => {
    // 1. Get all items from the current page's groups
    const allItemsOnPage = paginatedGroups.flatMap(([_, items]) => items);

    // 2. Use a Map to ensure unique items (just in case)
    // keyed by the unique combination of Assignment and Employee
    const uniqueItems = Array.from(
      new Map(
        allItemsOnPage.map((item) => [`${item.kpiAssignmentsId}_${item.employeeId}`, item])
      ).values()
    );

    const groups = {};
    uniqueItems.forEach((item) => {
      const p = item.perspective || "Other";
      if (!groups[p]) groups[p] = [];
      groups[p].push(item);
    });

    return groups;
  }, [paginatedGroups]);

  // Updated column styles for the "Modern" look
  const columnStyles = useMemo(
    () => ({
      // We remove fixed widths for names to allow "seeing fully"
      name: { minWidth: 200, fontWeight: 600 },
      formula: { width: 120, color: "text.secondary", fontSize: "0.75rem" },
      weight: { width: 60, textAlign: "center" },
      target: { width: 120, textAlign: "center" },
      achievement: { width: 100, textAlign: "center" },
      result: { width: 80, textAlign: "center" },
      actions: { width: 130, textAlign: "center" },
    }),
    []
  );

  const columns = useMemo(
    () => [
      ...(view !== "byEmployee" ? [{ label: t("photo"), style: columnStyles.photo }] : []),
      { label: t("user.assess.columns.name"), style: columnStyles.name },
      { label: t("user.assess.columns.Formula"), style: columnStyles.formula },
      { label: t("user.assess.columns.accountability"), style: columnStyles.formula },
      { label: t("user.assess.columns.Weight"), style: columnStyles.weight },
      { label: t("user.functional.target"), style: columnStyles.target },
      { label: t("user.myScorecard.selfAchievement"), style: columnStyles.selfAchievement },

      ...(isPrimary
        ? [
            {
              label: t("user.myScorecard.secondaryAchievement"),
              style: columnStyles.secondaryAchievement,
            },
          ]
        : []),
      { label: t("user.assess.columns.Achievement"), style: columnStyles.achievement },
      { label: t("user.assess.columns.Result"), style: columnStyles.result },
      { label: t("user.assess.columns.Status"), style: columnStyles.status },
      { label: t("user.assess.columns.Trend"), style: columnStyles.trend },
      // { label: "Evidence", style: {} },
      { label: t("user.assess.columns.evidence"), style: columnStyles.evidence },
      { label: t("user.assess.columns.assessorEvidence"), style: columnStyles.evidence },
      {
        label: t("user.assess.columns.functionalAssessment"),
        style: columnStyles.functionalAssessor,
      },
    ],
    [view, isPrimary, t, columnStyles]
  );

  if (loading) return <LoadingScreen />;

  const isArabic = i18n.language.startsWith("ar");

  return (
    <Box p={3}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        flexWrap="wrap"
        gap={2}
      >
        <ToggleButtonGroup
          value={view}
          exclusive
          onChange={(e, value) => {
            if (value) {
              setView(value);
              setEmployeeFilter("");
            }
          }}
        >
          <ToggleButton value="byEmployee">{t("user.functional.byEmployee")}</ToggleButton>
          <ToggleButton value="byKPI">{t("user.functional.byKPI")}</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <Box display="flex" flexWrap="wrap" gap={2} mb={2} alignItems="center">
        {/* Filter by Employee Name */}
        <Autocomplete
          size="small"
          options={
            view === "byEmployee"
              ? [...new Set(data?.map((item) => item.employeeName))]
              : [...new Set(data?.map((item) => item.kpiName))]
          }
          value={employeeFilter}
          onChange={(event, newValue) => setEmployeeFilter(newValue || "")}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t(
                view === "byEmployee"
                  ? t("user.functional.byEmployee")
                  : t("user.functional.filterByKPI")
              )}
            />
          )}
          sx={{ minWidth: 250 }}
          freeSolo
        />

        {/* Checkbox for unassessed only */}
        <FormControlLabel
          control={
            <Checkbox
              checked={showOnlyUnassessed}
              onChange={(e) => setShowOnlyUnassessed(e.target.checked)}
            />
          }
          label={t("user.functional.onlyUnassessed")}
        />
      </Box>

      {paginatedGroups?.map(([groupKey, items]) => {
        const itemsByPerspective = items.reduce((acc, item) => {
          const p = item.perspective || "Other";
          if (!acc[p]) acc[p] = [];
          acc[p].push(item);
          return acc;
        }, {});

        return (
          <Paper key={groupKey} sx={{ p: 2, mb: 3 }}>
            {/* HEADER */}
            <Box
              onClick={() => setExpandedEmployee(expandedEmployee === groupKey ? null : groupKey)}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                cursor: "pointer",
              }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                {view === "byEmployee" && (
                  <Avatar
                    src={items[0]?.photo ? `data:image/jpeg;base64,${items[0].photo}` : undefined}
                  />
                )}

                <Box>
                  <Typography variant="h6">{groupKey}</Typography>
                  {view === "byEmployee" && items[0]?.jobName && (
                    <Typography variant="body2" color="text.secondary">
                      {items[0].jobName}
                    </Typography>
                  )}
                </Box>

                <Chip
                  label={
                    view === "byEmployee" ? `${items.length} KPIs` : `${items.length} Employees`
                  }
                  size="small"
                />
              </Box>

              <IconButton>
                {expandedEmployee === groupKey ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
              </IconButton>
            </Box>

            {/* ONLY RENDER TABLE IF EXPANDED */}
            {expandedEmployee === groupKey && (
              <Box mt={2}>
                {isMobile ? (
                  <Stack spacing={3}>
                    {Object.entries(itemsByPerspective).map(([perspective, perspectiveItems]) => (
                      <Box key={perspective}>
                        {/* PERSPECTIVE HEADER */}
                        <Typography
                          variant="overline"
                          sx={{
                            fontWeight: 800,
                            color: "primary.main",
                            backgroundColor: "#f0f4f8",
                            px: 2,
                            py: 0.5,
                            borderRadius: 1,
                            display: "block",
                            mb: 1,
                            borderLeft: "4px solid",
                            borderColor: "primary.main",
                          }}
                        >
                          {/* {perspective} */}
                          {t(`user.assess.perspectives.${perspective}`)}
                        </Typography>

                        <Stack spacing={2}>
                          {perspectiveItems.map((item) => {
                            const key = `${item.kpiAssignmentsId}_${item.employeeId}`;
                            const name = view === "byEmployee" ? item.kpiName : item.employeeName;
                            return (
                              <Paper
                                key={key}
                                elevation={2}
                                sx={{ p: 2, borderRadius: 2, border: "1px solid #eee" }}
                              >
                                {/* 1. HEADER: Name + Description + Comments */}
                                <Box
                                  display="flex"
                                  justifyContent="space-between"
                                  alignItems="flex-start"
                                  mb={1.5}
                                >
                                  <Box sx={{ flex: 1 }}>
                                    <Typography
                                      variant="subtitle1"
                                      sx={{ fontWeight: 700, lineHeight: 1.2 }}
                                    >
                                      {name}
                                    </Typography>

                                    {/* Display jobName if in KPI view */}
                                    {view === "byKpi" && item.jobName && (
                                      <Typography
                                        variant="caption"
                                        color="textSecondary"
                                        sx={{ display: "block", mt: 0.5 }}
                                      >
                                        <b>{t("user.myScorecard.jobTitle")}:</b> {item.jobName}
                                      </Typography>
                                    )}

                                    <Typography
                                      variant="caption"
                                      color="textSecondary"
                                      sx={{
                                        fontStyle: "italic",
                                        display: "block",
                                        mt: view === "byKpi" ? 0.5 : 0,
                                      }}
                                    >
                                      <b>{t("user.myScorecard.formula")}:</b>{" "}
                                      {item.kpiFormula === "Regular"
                                        ? t("user.myScorecard.regular")
                                        : t("user.myScorecard.reverse")}
                                    </Typography>
                                  </Box>
                                  <Box display="flex">
                                    <DescriptionPopover
                                      title={name}
                                      description={item.description}
                                    />
                                    <CommentButton
                                      item={item}
                                      isPrimary={isPrimary}
                                      onSave={(val) => handleSaveComment(key, val)}
                                    />
                                  </Box>
                                </Box>

                                <Divider sx={{ mb: 1.5 }} />

                                {/* 2. DATA GRID */}
                                <Grid container spacing={1}>
                                  <Grid item xs={6}>
                                    <Typography
                                      variant="caption"
                                      color="textSecondary"
                                      display="block"
                                    >
                                      {t("user.assess.columns.Weight")}
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                      {item.weight}%
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={6}>
                                    <Typography
                                      variant="caption"
                                      color="textSecondary"
                                      display="block"
                                    >
                                      {t("user.assess.columns.Result")}
                                    </Typography>
                                    <Box display="flex" alignItems="center" gap={0.5}>
                                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                        {item.averageResult?.toFixed(1)}%
                                      </Typography>
                                      <Box
                                        sx={{
                                          width: 10,
                                          height: 10,
                                          borderRadius: "50%",
                                          bgcolor: getStatusColor(item.averageResult),
                                        }}
                                      />
                                      {getTrendIcon(item.trend, item.kpiAchievement)}
                                    </Box>
                                  </Grid>

                                  <Grid item xs={6}>
                                    <Typography
                                      variant="caption"
                                      color="textSecondary"
                                      display="block"
                                    >
                                      {t("user.functional.target")}
                                    </Typography>
                                    {isPrimary ? (
                                      <TextField
                                        type="number"
                                        size="small"
                                        value={targets[key] ?? ""}
                                        onChange={(e) => handleTargetChange(key, e.target.value)}
                                        onBlur={() => handleUpdateKpi(key, "target")}
                                        sx={{ mt: 0.5, maxWidth: 100 }}
                                      />
                                    ) : (
                                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {item.kpiTarget}
                                      </Typography>
                                    )}
                                  </Grid>

                                  <Grid item xs={6}>
                                    <Typography
                                      variant="caption"
                                      color="textSecondary"
                                      display="block"
                                    >
                                      {t("user.assess.columns.Achievement")}
                                    </Typography>

                                    <Box>
                                      <TextField
                                        type="number"
                                        size="small"
                                        value={achievements[key] ?? ""}
                                        disabled={item.source?.toLowerCase() === "automated"}
                                        onChange={(e) =>
                                          handleAchievementChange(key, e.target.value)
                                        }
                                        onBlur={() => handleUpdateKpi(key, "achievement")}
                                        sx={{
                                          mt: 0.5,
                                          maxWidth: 100,
                                          "& .MuiOutlinedInput-root": {
                                            ...(achievements[key]
                                              ? { "& fieldset": { borderColor: "success.main" } }
                                              : {}),
                                          },
                                        }}
                                      />

                                      {item.source?.toLowerCase() === "automated" && (
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            display: "block",
                                            mt: 0.5,
                                            color: "text.secondary",
                                            fontSize: "11px",
                                          }}
                                        >
                                          Automated
                                        </Typography>
                                      )}
                                    </Box>
                                  </Grid>
                                </Grid>

                                <Box sx={{ mt: 2, p: 1, bgcolor: "#f9fafb", borderRadius: 1 }}>
                                  <Typography
                                    variant="caption"
                                    sx={{ color: "text.secondary", display: "block", mb: 1 }}
                                  >
                                    {t("user.myScorecard.selfAchievement")}:{" "}
                                    <b>{item.selfAchievement || "-"}</b>
                                    {isPrimary &&
                                      ` | ${t("user.myScorecard.secondaryAchievement")}: `}
                                    {isPrimary && <b>{item.secondaryAchievement || "-"}</b>}
                                  </Typography>
                                </Box>

                                {/* 3. ACTIONS: EVIDENCE & ASSESSMENTS */}
                                <Box
                                  display="flex"
                                  justifyContent="space-between"
                                  alignItems="center"
                                  mt={2}
                                >
                                  <Box display="flex" gap={1} alignItems="center">
                                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                      {t("user.assess.columns.evidence")}:
                                    </Typography>

                                    {/* Self Evidence */}
                                    <EvidenceDownloadButton
                                      fileName={item.evidence}
                                      performanceId={item.performanceId}
                                      isSelfEvidence={true}
                                      myScorecardView={false}
                                    />

                                    {/* Assessor Evidence (Download + Delete + Upload) */}
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        borderLeft: "1px solid #ddd",
                                        pl: 1,
                                        gap: 0.5,
                                      }}
                                    >
                                      <EvidenceDownloadButton
                                        fileName={item.assessorEvidence}
                                        performanceId={item.performanceId}
                                        isSelfEvidence={false}
                                        myScorecardView={false}
                                      />

                                      {isPrimary && (
                                        <>
                                          {item.assessorEvidence && (
                                            <IconButton
                                              size="small"
                                              color="error"
                                              onClick={() => handleDeleteEvidence(item, false)}
                                            >
                                              <Clear fontSize="small" />
                                            </IconButton>
                                          )}
                                          <input
                                            type="file"
                                            id={`mobile-upload-${key}`}
                                            hidden
                                            onChange={(e) => handleFileAttach(e, item, false)}
                                          />
                                          <label htmlFor={`mobile-upload-${key}`}>
                                            <IconButton
                                              component="span"
                                              size="small"
                                              color={item.assessorEvidence ? "success" : "default"}
                                            >
                                              <AttachFile fontSize="small" />
                                            </IconButton>
                                          </label>
                                        </>
                                      )}
                                      <CommentButton
                                        item={item}
                                        isPrimary={true}
                                        isAdminScorecardView={true}
                                        currentUser={employee}
                                        // Using the specific name from your new backend logic
                                        allComments={item.adminPerformanceComments || []}
                                        // Disable only if there is no performanceId yet (not assessed)
                                        disabled={!item.performanceId}
                                      />
                                    </Box>
                                  </Box>
                                </Box>
                                <Box display="flex" alignItems="center">
                                  <Typography variant="caption" sx={{ mr: 0.5 }}>
                                    {t("user.assess.columns.functionalAssessment")}
                                  </Typography>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={(e) => handleOpenAssessments(e, item)}
                                  >
                                    {loadingKpiId === key ? (
                                      <CircularProgress size={16} />
                                    ) : (
                                      <KeyboardArrowDown />
                                    )}
                                  </IconButton>
                                </Box>
                              </Paper>
                            );
                          })}
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  //DESKTOP
                  <Box sx={{ overflowX: "auto" }}>
                    <Table size="small" sx={{ minWidth: 1100, borderCollapse: "separate" }}>
                      <TableBody>
                        {/* 1. STICKY HEADER ROW (Inside TableBody to fix spacing) */}
                        <TableRow
                          sx={{
                            position: "sticky",
                            top: 0,
                            zIndex: 10,
                            "& td": {
                              backgroundColor: "#ffffff !important", // 🔹 FIX: Solid background
                              fontWeight: "bold",
                              color: "grey.700",
                              borderBottom: "2px solid #eee",
                              py: 1.5,
                            },
                          }}
                        >
                          <TableCell sx={{ width: "25%" }}>
                            {t("user.functional.kpiInfo")}
                          </TableCell>
                          <TableCell sx={{ width: 60 }}>
                            {t("user.functional.formulaAccount")}
                          </TableCell>
                          <TableCell align="center" sx={{ width: 60 }}>
                            {t("user.assess.columns.Weight")}
                          </TableCell>
                          <TableCell align="center" sx={{ width: 110 }}>
                            {t("user.functional.target")}
                          </TableCell>
                          <TableCell align="center" sx={{ width: 100 }}>
                            {/* {t("user.myScorecard.selfAchievement")} */}
                            {isPrimary
                              ? t("user.assess.columns.selfAndSecondAssessment")
                              : t("user.myScorecard.selfAchievement")}
                          </TableCell>
                          <TableCell align="center" sx={{ width: 100 }}>
                            {t("user.assess.columns.Achievement")}
                          </TableCell>
                          <TableCell align="center" sx={{ width: 80 }}>
                            {t("user.assess.columns.Result")}
                          </TableCell>
                          <TableCell align="center" sx={{ width: 140 }}>
                            {t("user.assess.columns.evidence")}
                          </TableCell>
                          <TableCell align="center" sx={{ width: 50 }}>
                            {t("user.assess.columns.functionalAssessment")}
                          </TableCell>
                        </TableRow>

                        {/* 2. LOOP THROUGH PERSPECTIVES IN THIS PAPER */}
                        {Object.entries(itemsByPerspective).map(
                          ([perspective, perspectiveItems]) => (
                            <React.Fragment key={perspective}>
                              {/* PERSPECTIVE SUB-HEADER */}
                              <TableRow>
                                <TableCell
                                  colSpan={9}
                                  sx={
                                    isArabic
                                      ? {
                                          // ARABIC STYLE
                                          bgcolor: "#f1f3f4",
                                          py: 0.5,
                                          fontSize: "0.7rem",
                                          color: "primary.main",
                                          borderLeft: "4px solid #1976d2",

                                          // Font & rendering for Arabic
                                          fontFamily: `'Cairo', 'Noto Sans Arabic', 'Segoe UI', 'Tahoma', 'Arial', sans-serif`,
                                          direction: "rtl",
                                          unicodeBidi: "embed",

                                          // Avoid breaking letters
                                          letterSpacing: 0,
                                          fontWeight: 600,
                                          textTransform: "none",
                                          whiteSpace: "nowrap",
                                        }
                                      : {
                                          bgcolor: "#f1f3f4",
                                          py: 0.5,
                                          fontWeight: 800,
                                          fontSize: "0.65rem",
                                          color: "primary.main",
                                          // textTransform: "uppercase",
                                          letterSpacing: 1,
                                          borderLeft: "4px solid #1976d2",
                                          textTransform: "uppercase",
                                        }
                                  }
                                >
                                  {/* {perspective} */}
                                  {t(`user.assess.perspectives.${perspective}`)}
                                </TableCell>
                              </TableRow>

                              {perspectiveItems.map((item) => {
                                const key = `${item.kpiAssignmentsId}_${item.employeeId}`;

                                return (
                                  <KpiTableRow
                                    key={key}
                                    keyValue={key}
                                    item={item}
                                    perspective={perspective}
                                    view={view}
                                    isPrimary={isPrimary}
                                    targets={targets}
                                    achievements={achievements}
                                    compactInputStyle={compactInputStyle}
                                    handleTargetChange={handleTargetChange}
                                    handleAchievementChange={handleAchievementChange}
                                    handleUpdateKpi={handleUpdateKpi}
                                    handleSaveComment={handleSaveComment}
                                    handleConfOpen={handleConfOpen}
                                    handleFileAttach={handleFileAttach}
                                    handleOpenAssessments={handleOpenAssessments}
                                    getStatusColor={getStatusColor}
                                    getTrendIcon={getTrendIcon}
                                    t={t}
                                  />
                                );
                              })}
                            </React.Fragment>
                          )
                        )}
                      </TableBody>
                    </Table>
                  </Box>
                )}
              </Box>
            )}
          </Paper>
        );
      })}
      <Box display="flex" justifyContent="center" mt={2}>
        <Pagination
          count={Math.ceil(groupedEntries?.length / rowsPerPage)} // total pages
          page={page + 1} // MUI Pagination is 1-based, your state is 0-based
          onChange={(e, value) => setPage(value - 1)}
          color="primary"
        />
      </Box>
      <SnackbarWrapper
        ref={snackbarRef}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      />
      <ConfirmationDialog
        open={confOpen}
        onClose={() => setConfOpen(false)}
        message={"Are you sure you want to delete Evidence?"}
        onConfirm={handleDeleteEvidence}
      />

      <Menu
        anchorEl={menuAnchor}
        // Remove loadingKpiId === null. Open as soon as we have an anchor and an ID.
        open={Boolean(menuAnchor) && openKpiId !== null}
        onClose={handleCloseAssessments}
        // Ensure the menu doesn't get stuck behind other elements
        disablePortal={false}
        PaperProps={{
          sx: {
            maxHeight: 48 * 5,
            width: { xs: 240, sm: 320 },
            boxShadow: "0px 5px 15px rgba(0,0,0,0.2)",
            backgroundColor: "#fff !important", // Fix transparency
          },
        }}
      >
        {/* Show loading state INSIDE the menu if it's still fetching */}
        {loadingKpiId !== null ? (
          <Box sx={{ p: 2, display: "flex", justifyContent: "center" }}>
            <CircularProgress size={24} />
          </Box>
        ) : currentAssessments.length > 0 ? (
          currentAssessments.map((fa, idx) => (
            <MenuItem key={idx} sx={{ gap: 1, py: 1 }}>
              <Tooltip title={fa.assessorName}>
                <Typography variant="body2" noWrap sx={{ fontWeight: 600, maxWidth: 120 }}>
                  {fa.assessorName}
                </Typography>
              </Tooltip>

              <Tooltip title={fa.jobName}>
                <Typography
                  variant="caption"
                  noWrap
                  sx={{ maxWidth: 100, color: "text.secondary" }}
                >
                  ({fa.jobName})
                </Typography>
              </Tooltip>

              <Typography
                variant="body2"
                sx={{ ml: "auto", fontWeight: "bold", color: "primary.main" }}
              >
                {fa.achievement}
              </Typography>
            </MenuItem>
          ))
        ) : (
          <MenuItem disabled>{t("user.assess.noAssessments")}</MenuItem>
        )}
      </Menu>
    </Box>
  );
}

AssessorKpiPage.propTypes = {
  isPrimary: PropTypes.bool.isRequired,
};

ClampedCell.propTypes = {
  text: PropTypes.string.isRequired,
};

export default AssessorKpiPage;
